import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ProductCardLabel,
  ProductImageWrapper,
  ProductImageContainer,
} from './styles';

export interface ProductImageContainerProps {
  id?: string;
  src: string;
  alt: string;
  labels?: Array<{
    text: string;
    fontColor?: 'seedgreen' | 'snowwhite';
    pillColor?: string;
    position?:
      | 'left'
      | 'right'
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'
      | 'center'
      | 'top-center'
      | 'bottom-center';
    spacing?: string;
  }>;

  className?: string;
  linkUrl?: string; // Optional URL to make image clickable
  onClick?: () => void; // Optional click handler for image

  styleConfig?: {
    // styling
    padding?: string;
    backgroundColor?: string;

    // container sizing (controls the overall container size)
    containerWidth?: string;
    containerHeight?: string;
    containerMinHeight?: string;
    containerMaxWidth?: string;
    containerMaxHeight?: string;
    // flexbox layout determines how the image container grows and shrinks in relation to the parent and the other elements in the container
    flexGrow?: number;
    flexShrink?: number;
    flexBasis?: string;

    // image sizing (for Next.js Image intrinsic dimensions)
    // objectFit determines how the image is resized to fit the container
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    // objectPosition determines the position of the image within the container
    objectPosition?: string;
    // imageHeight and imageWidth are the intrinsic dimensions for Next.js Image
    imageHeight?: number;
    imageWidth?: number;
    // imageDisplaySize controls how much of the container the image takes up (default 80%)
    imageDisplaySize?: number; // percentage (0-100)
    // mobileImageDisplaySize controls image size on mobile (overrides imageDisplaySize)
    mobileImageDisplaySize?: number; // percentage (0-100)

    // Link URL to make image clickable
    linkUrl?: string;
  };
}

const ProductCardImageContainer: FC<ProductImageContainerProps> = ({
  id,
  src,
  alt,
  labels = [],
  className,
  linkUrl,
  onClick,
  styleConfig,
}) => {
  return (
    <ProductImageContainer id={id} className={className} config={styleConfig}>
      {labels.map((label, index) => (
        <ProductCardLabel
          key={`${label.text}-${index}`}
          size='small'
          fontColor={
            label.fontColor === 'seedgreen' ? 'primary' : 'inverse-primary'
          }
          pillColor={label.pillColor}
          position={label.position || 'left'}
          spacing={label.spacing}
        >
          {label.text}
        </ProductCardLabel>
      ))}
      <ProductImageWrapper
        containerHeight={styleConfig?.containerHeight || '100%'}
        className='product-image-wrapper'
      >
        {onClick ? (
          <div
            onClick={onClick}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            <Image
              src={src}
              alt={alt}
              fill
              style={{
                objectFit: styleConfig?.objectFit || 'contain',
                objectPosition: styleConfig?.objectPosition || 'center',
              }}
            />
          </div>
        ) : linkUrl || styleConfig?.linkUrl ? (
          <Link
            href={linkUrl || styleConfig?.linkUrl || ''}
            style={{ display: 'block', width: '100%', height: '100%' }}
          >
            <Image
              src={src}
              alt={alt}
              fill
              style={{
                objectFit: styleConfig?.objectFit || 'contain',
                objectPosition: styleConfig?.objectPosition || 'center',
              }}
            />
          </Link>
        ) : (
          <Image
            src={src}
            alt={alt}
            fill
            style={{
              objectFit: styleConfig?.objectFit || 'contain',
              objectPosition: styleConfig?.objectPosition || 'center',
            }}
          />
        )}
      </ProductImageWrapper>
    </ProductImageContainer>
  );
};

export default ProductCardImageContainer;
